import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import RevenueForecasting from './RevenueForecasting';

const BuyoutAndForecasting = ({ view: viewProp } = {}) => {
  const { projectVuid } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewFromQuery = searchParams.get('view') === 'revenue' ? 'revenue' : 'cost';
  const view = viewProp === 'revenue' || viewProp === 'cost' ? viewProp : viewFromQuery;
  const [project, setProject] = useState(null);
  const [accountingPeriods, setAccountingPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [budgetLines, setBudgetLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [projectLoading, setProjectLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState({});
  const [unsavedChanges, setUnsavedChanges] = useState({});
  const [saving, setSaving] = useState(false);
  const [notesModal, setNotesModal] = useState({ open: false, type: null, lineVuid: null });
  const [procoreEacAvailable, setProcoreEacAvailable] = useState(false);
  const [procoreEacError, setProcoreEacError] = useState('');
  const [procoreBudgetLink, setProcoreBudgetLink] = useState('');

  const baseURL = API_BASE_URL;

  useEffect(() => {
    if (projectVuid) {
      fetchProjectData();
      fetchAccountingPeriods();
    }
  }, [projectVuid]);

  useEffect(() => {
    if (projectVuid && selectedPeriod && view === 'cost') {
      fetchBuyoutData();
    }
  }, [projectVuid, selectedPeriod, view]);

  const fetchProjectData = async () => {
    setProjectLoading(true);
    try {
      const response = await axios.get(`${baseURL}/api/projects/${projectVuid}`);
      setProject(response.data);
    } catch (error) {
      console.error('Error fetching project data:', error);
      setError(error.response?.data?.error || 'Error fetching project data');
    } finally {
      setProjectLoading(false);
    }
  };

  const fetchAccountingPeriods = async () => {
    try {
      const response = await axios.get(`${baseURL}/api/accounting-periods`);
      const data = response.data;
      setAccountingPeriods(data);
      // Set default to current open period
      const openPeriod = data.find(p => p.status === 'open');
      if (openPeriod) {
        setSelectedPeriod(openPeriod.vuid);
      }
    } catch (error) {
      console.error('Error fetching accounting periods:', error);
      setError(error.response?.data?.error || 'Error fetching accounting periods');
    }
  };

  const fetchBuyoutData = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(
        `${baseURL}/api/projects/${projectVuid}/buyout-forecasting`,
        { params: { accounting_period_vuid: selectedPeriod } }
      );
      const result = response.data;
      if (result.success) {
        // Debug: Log the first budget line to see the structure
        if (result.data.budget_lines && result.data.budget_lines.length > 0) {
          const firstLine = result.data.budget_lines[0];
                                                                                                  }
        setBudgetLines(result.data.budget_lines);
        setProcoreEacAvailable(!!result.data.procore_eac_available);
        setProcoreEacError(result.data.procore_eac_error || '');
        const baseUrl = result.data.procore_base_url || 'https://sandbox.procore.com';
        if (result.data.procore_company_id && result.data.procore_project_id) {
          setProcoreBudgetLink(
            `${baseUrl}/webclients/host/companies/${result.data.procore_company_id}/projects/${result.data.procore_project_id}/tools/budgets`
          );
        } else {
          setProcoreBudgetLink('');
        }
      } else {
        setError(result.error || 'Failed to fetch buyout data');
      }
    } catch (error) {
      console.error('Error fetching buyout data:', error);
      setError(error.response?.data?.error || 'Error fetching buyout data');
    } finally {
      setLoading(false);
    }
  };

  const updateBuyoutStatus = async (budgetLineVuid, isBoughtOut, buyoutDate = null, buyoutAmount = null, notes = '') => {
    setUpdating(prev => ({ ...prev, [budgetLineVuid]: true }));
    
    try {
      const response = await axios.post(`${baseURL}/api/projects/${projectVuid}/buyout-forecasting`, {
        budget_line_vuid: budgetLineVuid,
        accounting_period_vuid: selectedPeriod,
        is_bought_out: isBoughtOut,
        buyout_date: buyoutDate,
        buyout_amount: buyoutAmount,
        notes: notes,
        created_by: 'Current User' // In a real app, this would come from auth context
      });

      if (response.data?.success) {
        // Refresh the data to show updated buyout status
        await fetchBuyoutData();
      } else {
        alert(`Error: ${response.data?.error || 'Failed to update buyout status'}`);
      }
    } catch (error) {
      console.error('Error updating buyout status:', error);
      alert(`Error: ${error.response?.data?.error || 'Error updating buyout status'}`);
    } finally {
      setUpdating(prev => ({ ...prev, [budgetLineVuid]: false }));
    }
  };

  const handleBuyoutToggle = (budgetLineVuid, isBoughtOut) => {
    // Update local state without saving to database
    setBudgetLines(prevLines => 
      prevLines.map(line => 
        line.budget_line_vuid === budgetLineVuid 
          ? { 
              ...line, 
              buyout: { 
                ...line.buyout, 
                is_bought_out: isBoughtOut,
                buyout_date: isBoughtOut ? new Date().toISOString().split('T')[0] : null
              }
            }
          : line
      )
    );
    
    // Track unsaved changes
    setUnsavedChanges(prev => ({
      ...prev,
      [budgetLineVuid]: {
        is_bought_out: isBoughtOut,
        buyout_date: isBoughtOut ? new Date().toISOString().split('T')[0] : null
      }
    }));
  };

  const saveAllBuyoutData = async () => {
    if (budgetLines.length === 0) {
      alert('No budget lines to save.');
      return;
    }

    setSaving(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      // Save buyout and forecasting data for ALL budget lines (not just changed ones)
      for (const line of budgetLines) {
        try {
          // Get unsaved changes for this line
          const changes = unsavedChanges[line.budget_line_vuid] || {};
          
          // Get override values (use unsaved changes if present, otherwise use existing values)
          const field_etc_override = changes.field_etc_override !== undefined 
            ? changes.field_etc_override 
            : (line.buyout?.field_etc_override ?? null);
          const field_etc_override_notes = changes.field_etc_override_notes !== undefined
            ? changes.field_etc_override_notes
            : (line.buyout?.field_etc_override_notes ?? null);
          const executive_etc_override = changes.executive_etc_override !== undefined 
            ? changes.executive_etc_override 
            : (line.buyout?.executive_etc_override ?? null);
          const executive_etc_override_notes = changes.executive_etc_override_notes !== undefined
            ? changes.executive_etc_override_notes
            : (line.buyout?.executive_etc_override_notes ?? null);
          
          // Validate: If override values are provided, notes must be provided
          if (field_etc_override !== null && field_etc_override !== '') {
            if (!field_etc_override_notes || field_etc_override_notes.trim() === '') {
              alert(`Budget line ${line.cost_code?.code || line.budget_line_vuid}: Notes are required when providing a Field ETC Override value.`);
              errorCount++;
              continue;
            }
          }
          
          if (executive_etc_override !== null && executive_etc_override !== '') {
            if (!executive_etc_override_notes || executive_etc_override_notes.trim() === '') {
              alert(`Budget line ${line.cost_code?.code || line.budget_line_vuid}: Notes are required when providing an Executive ETC Override value.`);
              errorCount++;
              continue;
            }
          }
          
          // Recalculate ETC based on overrides
          let etc_amount = line.etc_amount;
          if (executive_etc_override !== null && executive_etc_override !== '') {
            etc_amount = executive_etc_override;
          } else if (field_etc_override !== null && field_etc_override !== '') {
            etc_amount = field_etc_override;
          }
          
          // Recalculate EAC = Actuals ETC
          const eac_amount = line.actuals_amount + etc_amount;
          
          const response = await axios.post(`${baseURL}/api/projects/${projectVuid}/buyout-forecasting`, {
            budget_line_vuid: line.budget_line_vuid,
            accounting_period_vuid: selectedPeriod,
            is_bought_out: line.buyout.is_bought_out,
            buyout_date: line.buyout.buyout_date,
            buyout_amount: line.buyout.buyout_amount,
            notes: line.buyout.notes || '',
            created_by: 'Current User',
            // Include override values and notes
            field_etc_override: field_etc_override,
            field_etc_override_notes: field_etc_override_notes,
            executive_etc_override: executive_etc_override,
            executive_etc_override_notes: executive_etc_override_notes,
            // Include calculated values (recalculated with overrides)
            etc_amount: etc_amount,
            eac_amount: eac_amount,
            buyout_savings: line.buyout_savings,
            actuals_amount: line.actuals_amount,
            committed_amount: line.committed_amount,
            total_committed_amount: line.total_committed_amount
          });

          if (response.data?.success) {
            successCount++;
          } else {
            console.error(`Error saving budget line ${line.budget_line_vuid}:`, response.data?.error);
            errorCount++;
          }
        } catch (error) {
          console.error(`Error saving budget line ${line.budget_line_vuid}:`, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        // Clear unsaved changes and refresh data
        setUnsavedChanges({});
        // Small delay to ensure database commit is complete
        await new Promise(resolve => setTimeout(resolve, 100));
        await fetchBuyoutData();
        
        if (errorCount === 0) {
          alert(`Successfully saved buyout and forecasting data for ${successCount} budget line(s)!`);
        } else {
          alert(`Saved ${successCount} record(s), but ${errorCount} failed. Please try again.`);
        }
      } else {
        alert('Failed to save any records. Please try again.');
      }
    } catch (error) {
      console.error('Error saving buyout data:', error);
      alert('Error saving buyout data. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatCostCodeDisplay = (code) => {
    if (!code) {
      return code;
    }
    const trimmed = code.trim();
    if (trimmed.includes('.')) {
      const parts = trimmed.split('.').filter(Boolean);
      return parts.length > 1 ? parts.slice(1).join('.') : trimmed;
    }
    if (trimmed.includes('-')) {
      const parts = trimmed.split('-').filter(Boolean);
      return parts.length > 1 ? parts.slice(1).join('-') : trimmed;
    }
    const parts = trimmed.split(/\s+/);
    return parts.length > 1 ? parts.slice(1).join(' ') : trimmed;
  };

  const getSelectedPeriodName = () => {
    const period = accountingPeriods.find(p => p.vuid === selectedPeriod);
    return period ? `${period.month}/${period.year}` : 'Select Period';
  };

  const toNumber = (value) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const getEffectiveEtcAmount = (line) => {
    const changes = unsavedChanges[line.budget_line_vuid] || {};
    const execOverride = changes.executive_etc_override !== undefined
      ? changes.executive_etc_override
      : line.buyout?.executive_etc_override;
    const fieldOverride = changes.field_etc_override !== undefined
      ? changes.field_etc_override
      : line.buyout?.field_etc_override;

    const actualsAmount = toNumber(line.actuals_amount);
    const budgetedAmount = toNumber(line.budgeted_amount);
    const totalCommittedAmount = toNumber(line.total_committed_amount);

    let calculatedEtc = 0;
    if (totalCommittedAmount > budgetedAmount) {
      calculatedEtc = totalCommittedAmount - actualsAmount;
    } else if (totalCommittedAmount > 0 && line.buyout?.is_bought_out) {
      calculatedEtc = totalCommittedAmount - actualsAmount;
    } else {
      calculatedEtc = budgetedAmount - actualsAmount;
    }

    if (execOverride !== null && execOverride !== undefined && execOverride !== '') {
      return toNumber(execOverride);
    }
    if (fieldOverride !== null && fieldOverride !== undefined && fieldOverride !== '') {
      return toNumber(fieldOverride);
    }
    return calculatedEtc;
  };

  const baselineTotals = budgetLines.reduce(
    (totals, line) => {
      totals.budgeted += toNumber(line.budgeted_amount);
      totals.etc += toNumber(line.etc_amount);
      totals.eac += toNumber(line.eac_amount);
      totals.committed += toNumber(line.total_committed_amount);
      totals.buyoutSavings += toNumber(line.buyout_savings);
      return totals;
    },
    { budgeted: 0, etc: 0, eac: 0, committed: 0, buyoutSavings: 0 }
  );

  const summaryTotals = budgetLines.reduce(
    (totals, line) => {
      const budgetedAmount = toNumber(line.budgeted_amount);
      const totalCommittedAmount = toNumber(line.total_committed_amount);
      const buyoutSavings = toNumber(line.buyout_savings);
      const actualsAmount = toNumber(line.actuals_amount);
      const etcAmount = getEffectiveEtcAmount(line);

      totals.budgeted += budgetedAmount;
      totals.etc += etcAmount;
      totals.eac += actualsAmount + etcAmount;
      totals.committed += totalCommittedAmount;
      totals.buyoutSavings += buyoutSavings;
      return totals;
    },
    { budgeted: 0, etc: 0, eac: 0, committed: 0, buyoutSavings: 0 }
  );

  const hasUnsavedChanges = Object.keys(unsavedChanges).length > 0;
  const totalsDifferFromSaved = Math.abs(summaryTotals.eac - baselineTotals.eac) > 0.01
    || Math.abs(summaryTotals.etc - baselineTotals.etc) > 0.01;

  const setForecastView = (nextView) => {
    const next = new URLSearchParams(searchParams);
    if (nextView === 'revenue') next.set('view', 'revenue');
    else next.delete('view');
    setSearchParams(next);
  };

  if (!project) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">
          {projectLoading ? 'Loading project data...' : (error || 'Unable to load project data')}
        </div>
      </div>
    );
  }

  return (
    <div className="forecasting-page w-full px-4 py-4">
      {/* Header */}
      <div className="buyout-forecasting-page-header mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">Forecasting</h1>
            <p className="mt-2 text-lg text-gray-600">
              {project.project_number} - {project.project_name}
            </p>
          </div>
          <button
            onClick={() => navigate(`/projects/${projectVuid}`)}
            className="bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-md"
          >
            ← Back to Project
          </button>
        </div>
      </div>

      {viewProp ? null : (
      <div className="forecasting-view-tabs mb-6">
        <button
          type="button"
          className={view === 'revenue' ? 'is-active' : ''}
          onClick={() => setForecastView('revenue')}
        >
          Revenue Forecasting
        </button>
        <button
          type="button"
          className={view === 'cost' ? 'is-active' : ''}
          onClick={() => setForecastView('cost')}
        >
          Cost Forecasting
        </button>
      </div>
      )}

      {/* Period Selection */}
      <div className="mb-6">
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center space-x-4">
            <label htmlFor="period-select" className="text-sm font-medium text-gray-700">
              Accounting Period:
            </label>
            <select
              id="period-select"
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select Period</option>
              {accountingPeriods.map((period) => (
                <option key={period.vuid} value={period.vuid}>
                  {period.month}/{period.year} ({period.status})
                </option>
              ))}
            </select>
            {selectedPeriod && (
              <span className="text-sm text-gray-500">
                Current Period: {getSelectedPeriodName()}
              </span>
            )}
          </div>
        </div>
      </div>

      {view === 'revenue' ? (
        <RevenueForecasting
          projectVuid={projectVuid}
          selectedPeriod={selectedPeriod}
          periodLabel={getSelectedPeriodName()}
        />
      ) : (
      <>
      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">{error}</div>
        </div>
      )}
      {!error && procoreEacError && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="text-yellow-800">Procore EAC unavailable: {procoreEacError}</div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center h-32">
          <div className="text-gray-500">Loading buyout data...</div>
        </div>
      )}

      {/* Budget Lines Table */}
      {!loading && !error && budgetLines.length > 0 && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium text-gray-900">Budget Lines</h2>
                <p className="text-sm text-gray-600">
                  Track buyout status for each budget line in {getSelectedPeriodName()}
                </p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    // Refresh data to show current state
                    fetchBuyoutData();
                  }}
                  className="btn-claret"
                  >
                  Refresh Data
                </button>
                <button
                  onClick={saveAllBuyoutData}
                  disabled={saving || budgetLines.length === 0}
                  className={`font-medium py-2 px-4 rounded-md text-sm ${
                    saving || budgetLines.length === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  {saving ? 'Saving...' : 'Save Cost Forecasting Data'}
                </button>
              </div>
            </div>
          </div>
          
          <div className="forecasting-table-scroll">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cost Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cost Type
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Budget Amount
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Budgeted (w/ CO)
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Committed
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Change Orders
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Committed
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Buyout Savings
                  </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actuals
                    </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ETC
                  </th>
                    {procoreEacAvailable && (
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {procoreBudgetLink ? (
                          <a
                            href={procoreBudgetLink}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 hover:text-blue-800"
                          >
                            Procore EAC
                          </a>
                        ) : (
                          'Procore EAC'
                        )}
                      </th>
                    )}
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Field ETC Override
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Field Notes
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Executive ETC Override
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Executive Notes
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    EAC
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bought Out
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {budgetLines.map((line) => (
                  <tr key={line.budget_line_vuid} className={line.buyout.is_bought_out ? 'bg-green-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCostCodeDisplay(line.cost_code?.code) || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {line.cost_code?.description || 'No description'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {line.cost_type?.cost_type || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {line.cost_type?.abbreviation || ''}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      {formatCurrency(line.budget_amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                      {formatCurrency(line.budgeted_amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      {formatCurrency(line.committed_amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      {formatCurrency(line.budget_change_orders_amount ?? line.commitment_change_orders_amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                      {formatCurrency(line.total_committed_amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {line.buyout.is_bought_out ? (
                        <span className={line.buyout_savings > 0 ? 'text-green-600' : line.buyout_savings < 0 ? 'text-red-600' : 'text-gray-900'}>
                          {formatCurrency(line.buyout_savings)}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      {formatCurrency(line.actuals_amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      <span className={line.etc_amount < 0 ? 'text-red-600' : 'text-gray-900'}>
                        {formatCurrency(line.etc_amount)}
                      </span>
                    </td>
                    {procoreEacAvailable && (
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <span
                          className={
                            line.procore_eac != null && Math.abs(toNumber(line.procore_eac) - (toNumber(line.actuals_amount) + getEffectiveEtcAmount(line))) > 0.01
                              ? 'text-red-600 italic'
                              : 'text-gray-900'
                          }
                        >
                          {line.procore_eac != null ? formatCurrency(line.procore_eac) : '—'}
                        </span>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <input
                        type="number"
                        step="0.01"
                        value={
                          (() => {
                            const unsavedValue = unsavedChanges[line.budget_line_vuid]?.field_etc_override;
                            const savedValue = line.buyout?.field_etc_override;
                            const value = unsavedValue !== undefined ? unsavedValue : (savedValue != null ? savedValue : '');
                            // Only log if there's actually a saved value to debug
                            if (savedValue != null && savedValue !== undefined) {
                                                          }
                            return value;
                          })()
                        }
                        onChange={(e) => {
                          const value = e.target.value === '' ? null : (isNaN(parseFloat(e.target.value)) ? null : parseFloat(e.target.value));
                          setUnsavedChanges(prev => ({
                            ...prev,
                            [line.budget_line_vuid]: {
                              ...prev[line.budget_line_vuid],
                              field_etc_override: value
                            }
                          }));
                        }}
                        placeholder="—"
                        className="w-24 px-2 py-1 text-right border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {(line.buyout?.field_etc_override != null || unsavedChanges[line.budget_line_vuid]?.field_etc_override != null) ? (
                        <button
                          onClick={() => setNotesModal({ open: true, type: 'field', lineVuid: line.budget_line_vuid })}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-blue-100 transition-colors group relative"
                          data-tooltip="Add or edit field override notes"
                        >
                          {(unsavedChanges[line.budget_line_vuid]?.field_etc_override_notes ?? line.buyout?.field_etc_override_notes) ? (
                            <>
                              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></span>
                            </>
                          ) : (
                            <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          )}
                        </button>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <input
                        type="number"
                        step="0.01"
                        value={
                          (() => {
                            const unsavedValue = unsavedChanges[line.budget_line_vuid]?.executive_etc_override;
                            const savedValue = line.buyout?.executive_etc_override;
                            const value = unsavedValue !== undefined ? unsavedValue : (savedValue != null ? savedValue : '');
                            // Only log if there's actually a saved value to debug
                            if (savedValue != null && savedValue !== undefined) {
                                                          }
                            return value;
                          })()
                        }
                        onChange={(e) => {
                          const value = e.target.value === '' ? null : (isNaN(parseFloat(e.target.value)) ? null : parseFloat(e.target.value));
                          setUnsavedChanges(prev => ({
                            ...prev,
                            [line.budget_line_vuid]: {
                              ...prev[line.budget_line_vuid],
                              executive_etc_override: value
                            }
                          }));
                        }}
                        placeholder="—"
                        className="w-24 px-2 py-1 text-right border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {(line.buyout?.executive_etc_override != null || unsavedChanges[line.budget_line_vuid]?.executive_etc_override != null) ? (
                        <button
                          onClick={() => setNotesModal({ open: true, type: 'executive', lineVuid: line.budget_line_vuid })}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-purple-100 transition-colors group relative"
                          data-tooltip="Add or edit executive override notes"
                        >
                          {(unsavedChanges[line.budget_line_vuid]?.executive_etc_override_notes ?? line.buyout?.executive_etc_override_notes) ? (
                            <>
                              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></span>
                            </>
                          ) : (
                            <svg className="w-5 h-5 text-gray-400 group-hover:text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          )}
                        </button>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                      {formatCurrency(line.eac_amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center">
                        <input
                          type="checkbox"
                          checked={line.buyout.is_bought_out}
                          onChange={(e) => handleBuyoutToggle(line.budget_line_vuid, e.target.checked)}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                      {line.buyout.is_bought_out && (
                        <div className="text-xs text-green-600">
                          {line.buyout.buyout_date && (
                            <div>Date: {new Date(line.buyout.buyout_date).toLocaleDateString()}</div>
                          )}
                          {line.buyout.created_by && (
                            <div>By: {line.buyout.created_by}</div>
                          )}
                          {line.buyout.original_period && line.buyout.original_period !== selectedPeriod && (
                            <div className="text-blue-600">Originally bought out</div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && budgetLines.length === 0 && selectedPeriod && (
        <div className="text-center py-12">
          <div className="text-gray-500">
            No budget lines found for this project in {getSelectedPeriodName()}.
          </div>
        </div>
      )}

      {/* Summary */}
      {!loading && !error && budgetLines.length > 0 && (
        <div className="mt-8">
          {/* Save Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-blue-900">Save Cost Forecasting Data</h3>
                <p className="text-sm text-blue-800 mt-1">
                  Save all calculated ETC, EAC, and Buyout Savings data for this project and period. 
                  This data will be used in the WIP report for percentage complete calculations.
                </p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={saveAllBuyoutData}
                  disabled={saving || budgetLines.length === 0}
                  className={`btn-claret ${
                    saving || budgetLines.length === 0
                      ? 'opacity-50 cursor-not-allowed'
                      : ''
                  }`}
                  data-tooltip="Save all calculated buyout and forecasting data"
                >
                  <span>{saving ? 'Saving...' : 'Save All Data'}</span>
                </button>
                <button
                  onClick={() => {
                    fetchBuyoutData();
                    setUnsavedChanges({});
                  }}
                  className="btn-claret"
                  data-tooltip="Refresh buyout and forecasting data"
                >
                  <span>Refresh Data</span>
                </button>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          {(hasUnsavedChanges || totalsDifferFromSaved) && (
            <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="text-sm text-yellow-800">
                The totals below include unsaved changes. WIP uses the saved buyout & forecasting data for this period.
                Save your changes if you want WIP to match these totals.
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            <div className="bg-white shadow rounded-lg p-6">
              <div className="text-sm font-medium text-gray-500">Total Budget (w/ CO)</div>
              <div className="text-2xl font-bold text-gray-900">
                {formatCurrency(summaryTotals.budgeted)}
              </div>
            </div>
            <div className="bg-white shadow rounded-lg p-6">
              <div className="text-sm font-medium text-gray-500">Total ETC</div>
              <div className="text-2xl font-bold text-gray-900">
                {formatCurrency(summaryTotals.etc)}
              </div>
            </div>
            <div className="bg-white shadow rounded-lg p-6">
              <div className="text-sm font-medium text-gray-500">Total EAC</div>
              <div className="text-2xl font-bold text-gray-900">
                {formatCurrency(summaryTotals.eac)}
              </div>
            </div>
            <div className="bg-white shadow rounded-lg p-6">
              <div className="text-sm font-medium text-gray-500">Total Committed</div>
              <div className="text-2xl font-bold text-gray-900">
                {formatCurrency(summaryTotals.committed)}
              </div>
            </div>
            <div className="bg-white shadow rounded-lg p-6">
              <div className="text-sm font-medium text-gray-500">Total Buyout Savings</div>
              <div className="text-2xl font-bold">
                <span className={summaryTotals.buyoutSavings > 0 ? 'text-green-600' : summaryTotals.buyoutSavings < 0 ? 'text-red-600' : 'text-gray-900'}>
                  {formatCurrency(summaryTotals.buyoutSavings)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      </>
      )}

      {/* Notes Modal */}
      {notesModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">
                {notesModal.type === 'field' ? 'Field ETC Override Notes' : 'Executive ETC Override Notes'}
              </h3>
              <button
                onClick={() => setNotesModal({ open: false, type: null, lineVuid: null })}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-4">
              {(() => {
                const line = budgetLines.find(l => l.budget_line_vuid === notesModal.lineVuid);
                if (!line) return null;
                
                const overrideValue = notesModal.type === 'field' 
                  ? (unsavedChanges[line.budget_line_vuid]?.field_etc_override ?? line.buyout?.field_etc_override)
                  : (unsavedChanges[line.budget_line_vuid]?.executive_etc_override ?? line.buyout?.executive_etc_override);
                
                const currentNotes = notesModal.type === 'field'
                  ? (unsavedChanges[line.budget_line_vuid]?.field_etc_override_notes ?? line.buyout?.field_etc_override_notes ?? '')
                  : (unsavedChanges[line.budget_line_vuid]?.executive_etc_override_notes ?? line.buyout?.executive_etc_override_notes ?? '');
                
                return (
                  <>
                    <div className="mb-4">
                      <div className="text-sm text-gray-600 mb-2">
                        <strong>Cost Code:</strong> {line.cost_code?.code || 'N/A'} - {line.cost_code?.description || 'No description'}
                      </div>
                      <div className="text-sm text-gray-600">
                        <strong>Override Value:</strong> {overrideValue != null ? formatCurrency(overrideValue) : 'N/A'}
                      </div>
                    </div>
                    
                    {overrideValue != null && (
                      <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                        <p className="text-sm text-yellow-800">
                          <strong>Required:</strong> Notes are required when providing an override value. Please explain the reason for this override.
                        </p>
                      </div>
                    )}
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Notes {overrideValue != null && <span className="text-red-500">*</span>}
                      </label>
                      <textarea
                        value={currentNotes}
                        onChange={(e) => {
                          const fieldName = notesModal.type === 'field' ? 'field_etc_override_notes' : 'executive_etc_override_notes';
                          setUnsavedChanges(prev => ({
                            ...prev,
                            [notesModal.lineVuid]: {
                              ...prev[notesModal.lineVuid],
                              [fieldName]: e.target.value
                            }
                          }));
                        }}
                        placeholder="Enter notes explaining this override..."
                        required={overrideValue != null}
                        rows={6}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        {currentNotes.length} characters
                      </p>
                    </div>
                    
                    <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                      <button
                        onClick={() => setNotesModal({ open: false, type: null, lineVuid: null })}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          if (overrideValue != null && !currentNotes.trim()) {
                            alert('Notes are required when providing an override value.');
                            return;
                          }
                          setNotesModal({ open: false, type: null, lineVuid: null });
                        }}
                        className="btn-claret"
                        >
                        Save Notes
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuyoutAndForecasting;
